<script setup lang="ts">
import { ref, computed, toRaw, watch } from "vue";
import { SpeckleMaterial, speckleDeclaredUnit } from "@/types/Views/MappingWizard/SpeckleMaterial";
import { Checkbox } from "@/features/Inputs";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import { formatNumber } from "@/services/Utils/DecimalFormatter";
import { declaredUnitSymbol } from "@/utils/declaredUnitSymbol";

import { useEnum } from "@/stores/project/project.enum";

const enumStore = useEnum();
const { declaredUnits } = enumStore;

const props = defineProps<{
  cloneMaterial: SpeckleMaterial;
  totalDeclaredUnits: speckleDeclaredUnit;
  isMissingUnit: boolean;
  // The caller was already passing this while the prop did not exist, so the quantity input and the
  // preferred checkbox stayed editable on a read-only view — a Viewer could type a quantity into a
  // project they may not write to (RTLCA-5585).
  userCanEdit: boolean;
  enableUnitIds: number[];
}>();

const emits = defineEmits<{
  keyupEnter: [];
}>();

const cloneTotalDeclaredUnits = ref(JSON.parse(JSON.stringify(toRaw(props.cloneMaterial.totalDeclaredUnits || []))));

watch(
  () => props.cloneMaterial.totalDeclaredUnits?.length || 0,
  (newLength) => {
    const currentLength = cloneTotalDeclaredUnits.value.length;
    // If units were added (length increased) and we have the new units, update cloneTotalDeclaredUnits
    if (newLength > currentLength && props.cloneMaterial.totalDeclaredUnits) {
      // Only update if we're missing units (they were added after initialization)
      cloneTotalDeclaredUnits.value = JSON.parse(JSON.stringify(toRaw(props.cloneMaterial.totalDeclaredUnits)));
    }
  }
);

const handleTogglePreferred = (unit: speckleDeclaredUnit) => {
  props.cloneMaterial.totalDeclaredUnits.map((declaredUnit) => {
    if (declaredUnit.preferred) declaredUnit.wasChanged = true;
    declaredUnit.preferred = false;
  });

  unit.preferred = true;
  unit.wasChanged = true;
};

const modelForUnitNewValue = (unit: speckleDeclaredUnit, i: number) =>
  computed<number | null>({
    get: () => {
      const originalValue = cloneTotalDeclaredUnits.value[i]?.value;

      if (unit.value === originalValue) {
        return null;
      }
      return unit.value;
    },
    set: (val: number | null) => {
      const originalValue = cloneTotalDeclaredUnits.value[i]?.value;
      unit.value = val ?? originalValue;

      unit.wasChanged = val !== null && val !== originalValue;
    },
  });

// The persisted as-imported value for a unit (RTLCA-5038 backend snapshot), matched by declared
// unit. Read-only reference — independent of the in-session edits tracked by cloneTotalDeclaredUnits.
const originalValueFor = (declaredUnit: number): number | null =>
  props.cloneMaterial.originalDeclaredUnits?.find((original) => original.declaredUnit === declaredUnit)?.value ?? null;

const originalDisplayFor = (declaredUnit: number): string => {
  const value = originalValueFor(declaredUnit);
  return value !== null ? formatNumber(value) : "-";
};
</script>

<template>
  <div class="overflow-x-auto">
    <div class="rounded-8 bg-slate-0 t-small text-type-default min-w-[500px] overflow-hidden border border-slate-200">
      <!-- Header -->
      <div
        class="t-caption-bold text-type-muted grid grid-cols-12 items-center gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2.5 tracking-wide uppercase">
        <span class="col-span-2">Preferred</span>
        <span class="col-span-2">Unit</span>
        <!-- One editable Quantity column instead of a read-only "Value" plus a separate "Assign new
             value" input (RTLCA-5182). The old pair showed the same number twice and made you look in
             two places to answer "what quantity is this material?" — Quantity is now the field you
             type in, and Original stays as the read-only as-imported reference beside it. -->
        <span class="col-span-5">Quantity</span>
        <span class="col-span-3">Original</span>
      </div>

      <!-- Rows -->
      <div
        v-for="(unit, i) in props.cloneMaterial.totalDeclaredUnits || []"
        :key="unit.declaredUnit"
        class="duration-fast ease-standard grid grid-cols-12 items-center gap-2 border-b border-slate-200 px-4 py-2 transition-colors last:border-b-0 hover:bg-slate-50"
        :class="[
          !enableUnitIds.includes(unit.declaredUnit) && enableUnitIds.length !== 0 ? 'pointer-events-none opacity-50' : '',
        ]">
        <span class="col-span-2">
          <Checkbox
            label=""
            :name="`preferred-${unit.declaredUnit}`"
            :checked="unit.preferred ?? false"
            :disabled="!userCanEdit"
            @change="handleTogglePreferred(unit)" />
        </span>
        <span class="t-small-bold text-type-default col-span-2">
          {{ declaredUnitSymbol(declaredUnits[unit.declaredUnit]) }}
        </span>
        <div class="col-span-5">
          <NumberInput
            v-model:value="modelForUnitNewValue(unit, i).value"
            color="primary"
            :disabled="!userCanEdit"
            :placeholder="formatNumber(cloneTotalDeclaredUnits[i].value ?? 0)"
            :max-decimals="3"
            :error-msg="isMissingUnit ? 'Please enter quantity' : ''"
            size="w-1/2"
            @keyup.enter="() => emits('keyupEnter')" />
        </div>
        <span class="text-type-muted col-span-3">{{ originalDisplayFor(unit.declaredUnit) }}</span>
      </div>
    </div>
  </div>
</template>
